export default function MethodologySection() {
  return (
    <section className="relative w-full bg-background-100 py-24 md:py-32 lg:py-40 px-8 md:px-16 lg:px-24 xl:px-32">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end gap-8 mb-16 lg:mb-20">
          <div className="flex-1">
            <span className="inline-block text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium mb-6">
              Methodology
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-medium text-foreground-950 leading-[1.15]">
              How we approach <br />
              <span className="italic text-accent-500">every engagement</span>
            </h2>
          </div>
          <div className="lg:max-w-md">
            <p className="text-base text-foreground-500 leading-relaxed">
              A disciplined, repeatable process rooted in modern engineering standards — from initial analysis through architecture, implementation, and continuous refinement.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-background-50 rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20macro%20photograph%20of%20precision-layered%20materials%20with%20clean%20geometric%20edges%20and%20diffused%20natural%20lighting%2C%20warm%20bronze%20and%20ivory%20tones%2C%20architectural%20detail%20with%20layered%20depth%2C%20soft%20shadows%2C%20no%20text%20no%20people%2C%20minimalist%20editorial%20style&width=800&height=500&seq=method-01-feman&orientation=landscape"
              alt="Abstract precision-layered materials with clean geometric edges"
              className="w-full h-56 object-cover object-top"
            />
            <div className="p-8">
              <span className="inline-block px-3 py-1 bg-accent-100/70 rounded-full text-xs tracking-wide uppercase text-accent-700 font-medium mb-4">
                Phase One
              </span>
              <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-3 leading-tight">
                Analysis &amp; Architecture
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Deep understanding of requirements precedes all technical decisions. We define clear architectural boundaries, select appropriate patterns, and establish quality benchmarks before writing a single line of code.
              </p>
            </div>
          </div>

          <div className="bg-background-50 rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20photograph%20of%20interlocking%20precision%20components%20with%20warm%20charcoal%20and%20amber%20lighting%2C%20industrial%20engineering%20aesthetic%2C%20clean%20modular%20forms%20with%20soft%20shadows%2C%20no%20text%20no%20people%2C%20editorial%20minimalist%20composition&width=800&height=500&seq=method-02-feman&orientation=landscape"
              alt="Abstract interlocking precision components with warm charcoal and amber lighting"
              className="w-full h-56 object-cover object-top"
            />
            <div className="p-8">
              <span className="inline-block px-3 py-1 bg-accent-100/70 rounded-full text-xs tracking-wide uppercase text-accent-700 font-medium mb-4">
                Phase Two
              </span>
              <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-3 leading-tight">
                Implementation &amp; Integration
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Systematic construction with continuous testing and integration. Every component is built to specification, reviewed for quality, and validated against the architectural plan to ensure consistency and reliability.
              </p>
            </div>
          </div>

          <div className="bg-background-50 rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20photograph%20of%20polished%20refined%20surfaces%20with%20subtle%20texture%20variations%20and%20warm%20metallic%20accents%2C%20precision%20engineering%20aesthetic%2C%20soft%20bronze%20and%20cream%20tones%2C%20no%20text%20no%20people%2C%20minimalist%20editorial%20style&width=800&height=500&seq=method-03-feman&orientation=landscape"
              alt="Abstract polished refined surfaces with subtle texture variations"
              className="w-full h-56 object-cover object-top"
            />
            <div className="p-8">
              <span className="inline-block px-3 py-1 bg-accent-100/70 rounded-full text-xs tracking-wide uppercase text-accent-700 font-medium mb-4">
                Phase Three
              </span>
              <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-3 leading-tight">
                Refinement &amp; Evolution
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                Continuous improvement through measured iteration. We monitor, analyze, and refine based on real-world usage, evolving the system while preserving its architectural integrity and technical quality.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}